import { Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { 
  AdditionalAttributes, 
  PersonAttributes, 
  OpportunityAttributes,
  PropertyPurchaseAttributes,
  PropertyPurchaseFinancials,
  MenuAttributes,
  ProductSearchResultAttributes,
  ProductType,
  ProductDetails,
  PaygIncomeAttributes
} from '@shared/enums/customisation.enum';
import { CustomisationService } from '@shared/services/customisation/customisation.service';

@Directive({
  selector: '[appCustomisation]'
})
export class CustomisationDirective {
  private hasView = false;

  @Input() set appCustomisation(
    [fieldName]: [
      PersonAttributes | 
      AdditionalAttributes | 
      OpportunityAttributes | 
      PropertyPurchaseAttributes | 
      PropertyPurchaseFinancials | 
      MenuAttributes | 
      ProductSearchResultAttributes | 
      ProductType | 
      ProductDetails |
      PaygIncomeAttributes
    ]
  ) {
    if (Object.values(PersonAttributes).includes(fieldName as PersonAttributes)) {
      this.handlePersonAttributes(fieldName as PersonAttributes);
    }
  
    if (Object.values(OpportunityAttributes).includes(fieldName as OpportunityAttributes)) {
      this.handleOpportunityAttributes(fieldName as OpportunityAttributes);
    }
  
    if (Object.values(AdditionalAttributes).includes(fieldName as AdditionalAttributes)) {
      this.handleAdditionalAttributes(fieldName as AdditionalAttributes);
    }
  
    if (Object.values(PropertyPurchaseAttributes).includes(fieldName as PropertyPurchaseAttributes)) {
      this.handlePropertyPurchaseAttributes(fieldName as PropertyPurchaseAttributes);
    }
  
    if (Object.values(PropertyPurchaseFinancials).includes(fieldName as PropertyPurchaseFinancials)) {
      this.handlePropertyPurchaseFinancials(fieldName as PropertyPurchaseFinancials);
    }

    if (Object.values(MenuAttributes).includes(fieldName as MenuAttributes)) {
      this.handleMenuAttributes(fieldName as MenuAttributes);
    }

    if (Object.values(ProductSearchResultAttributes).includes(fieldName as ProductSearchResultAttributes)) {
      this.handleProductSearchResultAttributes(fieldName as ProductSearchResultAttributes);
    }

    if (Object.values(ProductType).includes(fieldName as ProductType)) {
      this.handleProductType(fieldName as ProductType);
    }

    if (Object.values(ProductDetails).includes(fieldName as ProductDetails)) {
      this.handleProductDetails(fieldName as ProductDetails);
    }

    if (Object.values(PaygIncomeAttributes).includes(fieldName as PaygIncomeAttributes)) {
      this.handlePaygIncomeAttributes(fieldName as PaygIncomeAttributes);
    } 
  }

  private handlePersonAttributes(fieldName: PersonAttributes) {
    let personAttributes: PersonAttributes[] = this.customisationService.getCustomisationForPersonDetails();
    if (personAttributes.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (personAttributes.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private handleOpportunityAttributes(fieldName: OpportunityAttributes) {
    let opportunityAttributes: OpportunityAttributes[] = this.customisationService.getCustomisationForOpportunityDetails();
    if (opportunityAttributes.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (opportunityAttributes.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private handleAdditionalAttributes(fieldName: AdditionalAttributes) {
    let additionalAttributes: AdditionalAttributes[] = this.customisationService.getCustomisationForAdditionalDetails();
    if (additionalAttributes.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (additionalAttributes.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private handlePropertyPurchaseAttributes(fieldName: PropertyPurchaseAttributes) {
    let propertyPurchaseAttributes: PropertyPurchaseAttributes[] = this.customisationService.getCustomisationForPropertyPurchaseDetails();
    if (propertyPurchaseAttributes.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (propertyPurchaseAttributes.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private handlePropertyPurchaseFinancials(fieldName: PropertyPurchaseFinancials) {
    let propertyPurchaseFinancials: PropertyPurchaseFinancials[] = this.customisationService.getCustomisationForPropertyPurchaseFinancials();
    if (propertyPurchaseFinancials.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (propertyPurchaseFinancials.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private handleMenuAttributes(fieldName: MenuAttributes) {
    let menuAttributes: MenuAttributes[] = this.customisationService.getCustomisationForSideMenu().menuAttributes;
    if (menuAttributes.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (menuAttributes.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private handleProductSearchResultAttributes(fieldName: ProductSearchResultAttributes) {
    let productSearchResultAttributes: ProductSearchResultAttributes[] = this.customisationService.getCustomisationForProductSearchResultDetails();
    if (productSearchResultAttributes.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (productSearchResultAttributes.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private handleProductType(fieldName: ProductType) {
    let productType: ProductType = this.customisationService.getCustomisationForProductType();
    if (productType.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (productType.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private handleProductDetails(fieldName: ProductDetails) {
    let productDetails: ProductDetails[] = this.customisationService.getCustomisationForProductDetails();
    if (productDetails.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (productDetails.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private handlePaygIncomeAttributes(fieldName: PaygIncomeAttributes) {
    let paygIncomeAttributes: PaygIncomeAttributes[] = this.customisationService.getCustomisationForPaygIncomeDetails();
    if (paygIncomeAttributes.indexOf(fieldName) >= 0 && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (paygIncomeAttributes.indexOf(fieldName) < 0 && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private customisationService: CustomisationService
  ) { }
}